import React, { useEffect, useState } from 'react';
import { dhfApi } from '../utils/dhfApi';
import '../styles/Dhf.css';

function flatten(nodes, depth = 0, out = []) {
  (nodes || []).forEach((p) => {
    out.push({ id: p.id, label: `${p.num} ${p.name}`, depth });
    flatten(p.children, depth + 1, out);
  });
  return out;
}

/**
 * "Place in document" on the shared Add Item screen (manual and ✨ Ask Paul alike, DHF v4.34.0).
 *
 * Offers the documents that already hold this level's items (`Root Document Id`) and the chosen
 * document's paragraphs; the item is then created under that paragraph instead of in the pool.
 * Each person's last paragraph per document is remembered by the server and preselected.
 *
 * Renders NOTHING when the level has no documents (filter without Root Document Id, or every
 * item in the pool) — the Add screen is then exactly what it was. `onChange(value)`:
 * {docId, parentId, parentLabel} | null (= the pool).
 */
export default function DocPlacement({ setupId, levelNum, blockId, onChange, disabled, title = 'Place in document' }) {
  const [docs, setDocs] = useState(null);
  const [docId, setDocId] = useState('');
  const [paras, setParas] = useState(null);
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    dhfApi.placement(setupId, levelNum, blockId)
      .then((r) => {
        if (!live) return;
        setDocs(r.documents || []);
        setDocId(r.default_doc || '');
      })
      .catch(() => { if (live) setDocs([]); });       // fail quiet: the pool, as before
    return () => { live = false; };
  }, [setupId, levelNum, blockId]);

  useEffect(() => {
    setParas(null);
    setParentId('');
    setError('');
    if (!docId) return undefined;
    let live = true;
    dhfApi.outline(setupId, docId)
      .then((r) => {
        if (!live) return;
        const list = flatten(r.paragraphs);
        setParas(list);
        const last = (docs || []).find((d) => d.id === docId)?.last?.parent_id;
        setParentId(list.some((p) => p.id === last) ? last : '');
      })
      .catch((e) => { if (live) { setParas([]); setError(e.message); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupId, docId]);

  useEffect(() => {
    const p = (paras || []).find((x) => x.id === parentId);
    onChange(docId && p ? { docId, parentId: p.id, parentLabel: p.label } : null);
    // `onChange` is a setState from the modal — stable; never a dependency (Note #59).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, parentId, paras]);

  if (!docs || docs.length === 0) return null;
  const last = docs.find((d) => d.id === docId)?.last;

  return (
    <section className="dhf-place" aria-label={title}>
      <div className="dhf-place-h">{title}</div>
      <div className="dhf-place-row">
        <label>
          Document
          <select value={docId} disabled={disabled} onChange={(e) => setDocId(e.target.value)}>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.name}{d.key ? ` · ${d.key}` : ''}</option>)}
            <option value="">— Pool (no document) —</option>
          </select>
        </label>
        {docId && (
          <label>
            Parent paragraph
            <select value={parentId} disabled={disabled || paras === null} onChange={(e) => setParentId(e.target.value)}>
              <option value="">{paras === null ? 'Loading the outline…' : '— Pool (no paragraph) —'}</option>
              {(paras || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {' '.repeat(p.depth * 3)}{p.label}{last?.parent_id === p.id ? '  · last used' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {error && <div className="dhf-place-note dhf-place-note--warn">Couldn’t read the document’s paragraphs: {error}</div>}
      {docId && paras && paras.length === 0 && !error && (
        <div className="dhf-place-note">This document has no paragraphs — the item goes to the pool.</div>
      )}
      {docId && parentId && <div className="dhf-place-note">Created at the end of that paragraph. Your choice is remembered for this document.</div>}
    </section>
  );
}
